import Dexie, { type Table } from "dexie";
import type { LocalAudioChunk, LocalRecordingSession } from "@/models";

const SessionColumnsToIndex = [
  "id" // Primary key
] as const satisfies readonly (keyof LocalRecordingSession)[];

const ChunkColumnsToIndex = [
  "chunkId", // Primary key
  "sessionId", // Used in .where("sessionId").equals()
  "sequenceNumber",
  "timeStamp"
] as const satisfies readonly (keyof LocalAudioChunk)[];

type SessionIndexes = (typeof SessionColumnsToIndex)[number];
type ChunkIndexes = (typeof ChunkColumnsToIndex)[number];

class RecordingsLocalDb extends Dexie {
  recordingSessions!: Table<LocalRecordingSession>;
  audioChunks!: Table<LocalAudioChunk>;

  constructor() {
    super("RecordingsLocalDb");

    // Version 16: Added sequenceNumber index for reliable chunk ordering.
    this.version(16).stores({
      recordingSessions: SessionColumnsToIndex.join(", "),
      audioChunks: ChunkColumnsToIndex.join(", ")
    });
  }

  // Strongly-typed wrapper methods
  recordingSessionsWhere<TIndex extends SessionIndexes>(index: TIndex) {
    return this.recordingSessions.where(index);
  }

  audioChunksWhere<TIndex extends ChunkIndexes>(index: TIndex) {
    return this.audioChunks.where(index);
  }
}

const recordingsLocalDb = new RecordingsLocalDb();

const createAudioChunk = async (chunk: LocalAudioChunk): Promise<void> => {
  await recordingsLocalDb.audioChunks.add(chunk);
};

const createRecordingSession = async (
  recording: LocalRecordingSession
): Promise<void> => {
  await recordingsLocalDb.recordingSessions.add(recording);
};

const getLastChunk = async (
  sessionId: string
): Promise<LocalAudioChunk | undefined> => {
  return await recordingsLocalDb
    .audioChunksWhere("sessionId")
    .equals(sessionId)
    .reverse()
    .first();
};

const updateChunk = async (chunk: LocalAudioChunk): Promise<void> => {
  await recordingsLocalDb.audioChunks.put(chunk);
};

const updateRecordingSession = async (
  recording: LocalRecordingSession
): Promise<void> => {
  await recordingsLocalDb.recordingSessions.put(recording);
};

const getUnsynchedRecordingSessions = async (): Promise<
  LocalRecordingSession[]
> => {
  return await recordingsLocalDb.recordingSessions
    .filter((rec) => rec.isSynchronized === false && rec.isOngoing === false)
    .toArray();
};

const getRecordingSessionsForProspect = async (
  prospectId: number
): Promise<LocalRecordingSession[]> => {
  return await recordingsLocalDb.recordingSessions
    .filter((rec) => rec.prospectId === prospectId)
    .toArray();
};

const getChunkSyncProgress = async (
  sessionId: string
): Promise<{ totalChunks: number; synchronizedChunks: number }> => {
  const chunks = await recordingsLocalDb
    .audioChunksWhere("sessionId")
    .equals(sessionId)
    .toArray();
  return {
    totalChunks: chunks.length,
    synchronizedChunks: chunks.filter((c) => c.isSynchronized).length
  };
};

const hasLastChunk = async (sessionId: string): Promise<boolean> => {
  const chunk = await recordingsLocalDb
    .audioChunksWhere("sessionId")
    .equals(sessionId)
    .filter((c) => c.isLastChunk)
    .first();
  return chunk !== undefined;
};

const getSortedChunksForRecordingSession = async (
  sessionId: string,
  unsynchronizedOnly = false
): Promise<LocalAudioChunk[]> => {
  return await recordingsLocalDb
    .audioChunksWhere("sessionId")
    .equals(sessionId)
    .filter((chunk) => !unsynchronizedOnly || !chunk.isSynchronized)
    .sortBy("sequenceNumber"); // we are now sorting by sequenceNumber instead of timestamp for reliable ordering
};

const deleteOfflineRecordingSessionAndChunks = async (
  sessionId: string
): Promise<boolean> => {
  try {
    await recordingsLocalDb.transaction(
      "rw",
      recordingsLocalDb.recordingSessions,
      recordingsLocalDb.audioChunks,
      async () => {
        await recordingsLocalDb.recordingSessions.delete(sessionId);
        await recordingsLocalDb
          .audioChunksWhere("sessionId")
          .equals(sessionId)
          .delete();
      }
    );
    return true;
  } catch (error) {
    console.error("Error deleting recording and chunks:", error);
    return false;
  }
};

export const localRecordingStorage = {
  createAudioChunk,
  createRecordingSession,
  getLastChunk,
  updateChunk,
  updateRecordingSession,
  getUnsynchedRecordingSessions,
  getRecordingSessionsForProspect,
  getChunkSyncProgress,
  hasLastChunk,
  getSortedChunksForRecordingSession,
  deleteOfflineRecordingSessionAndChunks
};
